import type { SignalsDescriptionResponseDto } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalsDescription";
import type { TrafficTemplateAlias } from "app/pages/sales-intelligence/sub-modules/signals/services/templates/traffic/types";
import trafficSignalFiltersService from "app/pages/sales-intelligence/sub-modules/signals/services/ui-signals/traffic/trafficSignalFiltersService";
import type { ComponentType } from "react";
import { useMemo } from "react";
import type { ICountryObject } from "app/services/CountryService";

import AccountTractionChangeFiltersView from "../../components/views/TrafficSignalFiltersView/AccountTractionChangeFiltersView/AccountTractionChangeFiltersView";
import NewMarketsFiltersView from "../../components/views/TrafficSignalFiltersView/NewMarketsFiltersView/NewMarketsFiltersView";
import TrafficSignalSubType from "../../models/traffic/TrafficSignalSubType";
import type TrafficSignalFiltersViewProps from "../../models/view/traffic/TrafficSignalFiltersViewProps";
import type TrafficSignalFiltersViewResolver from "../../models/view/traffic/TrafficSignalFiltersViewResolver";

type FiltersViewWithCountryProps = TrafficSignalFiltersViewProps & {
    countries: readonly ICountryObject[];
};

type FiltersViewWithTrafficChangesProps = Pick<SignalsDescriptionResponseDto, "trafficChanges">;

type FiltersViewWithNewMarketsProps = Pick<SignalsDescriptionResponseDto, "newMarkets">;

type FactoryDeps = Pick<SignalsDescriptionResponseDto, "trafficChanges" | "newMarkets"> & {
    countries: readonly ICountryObject[];
};

export default function createTrafficSignalFiltersViewResolver(
    deps: FactoryDeps,
): Readonly<TrafficSignalFiltersViewResolver> {
    const withCountries = <P extends FiltersViewWithCountryProps>(Component: ComponentType<P>) => {
        return function WrapperWithCountries(props: P) {
            return (
                <Component
                    {...props}
                    countries={deps.countries}
                />
            );
        };
    };

    const withTrafficChanges = <P extends TrafficSignalFiltersViewProps>(
        Component: ComponentType<P & FiltersViewWithTrafficChangesProps>,
    ) => {
        return function WrapperWithTrafficChanges(props: P) {
            const trafficChanges = useMemo(
                () =>
                    trafficSignalFiltersService.getFiltersOptionsBy(
                        deps.trafficChanges,
                        props.signalAlias as TrafficTemplateAlias,
                    ),
                [props.signalAlias],
            );

            return (
                <Component
                    {...props}
                    trafficChanges={trafficChanges}
                />
            );
        };
    };

    const withNewMarkets = <P extends TrafficSignalFiltersViewProps>(
        Component: ComponentType<P & FiltersViewWithNewMarketsProps>,
    ) => {
        return function WrapperWithNewMarkets(props: P) {
            const newMarkets = useMemo(
                () =>
                    trafficSignalFiltersService.getFiltersOptionsBy(
                        deps.newMarkets,
                        props.signalAlias as TrafficTemplateAlias,
                    ),
                [props.signalAlias],
            );

            return (
                <Component
                    {...props}
                    newMarkets={newMarkets}
                />
            );
        };
    };

    function getFiltersView(subType: TrafficSignalSubType) {
        switch (subType) {
            case TrafficSignalSubType.GettingTraction:
            case TrafficSignalSubType.LosingTraction:
            case TrafficSignalSubType.Engagement:
                return withCountries(withTrafficChanges(AccountTractionChangeFiltersView));
            case TrafficSignalSubType.NewMarkets:
                return withCountries(withNewMarkets(NewMarketsFiltersView));
            default:
                return undefined;
        }
    }

    return { getFiltersView };
}
